/**
 * Five tiles labelled "2048" are not five apps.
 *
 * The server models one Compute (the software) with many ComputeInstances
 * (each a stable URL with its own data). Rendered one-tile-per-instance, that
 * produced a home screen of identical tiles and the obvious question —
 * "what is different about these five?" — which is a question about our
 * database, not about somebody's apps.
 *
 * The honest answer is: nothing about the APP is different. The data is. So
 * the drift is absorbed where it belongs, one level down: one tile per App,
 * and the separate sets of data live inside it.
 *
 * Kept out of the component so the rule is testable on its own, and so the
 * ordering below is a stated decision rather than whatever the API returned.
 */

import type { AppGroup, PersonalApp } from "@/components/personal-apps/types";

function updatedAtMs(app: PersonalApp): number {
  const at = Date.parse(app.updated_at || app.created_at);
  return Number.isNaN(at) ? 0 : at;
}

/**
 * One group per Compute, newest activity first.
 *
 * Within a group the instances are ordered the same way, so `instances[0]` is
 * the one to resume. A launcher resumes; it does not shuffle. Ties fall back
 * to the instance id so the order is stable across reloads rather than
 * depending on however the rows came back.
 */
export function groupByApp(apps: PersonalApp[]): AppGroup[] {
  const groups = new Map<string, AppGroup>();
  for (const app of apps) {
    const existing = groups.get(app.compute_id);
    if (existing) {
      existing.instances.push(app);
      continue;
    }
    groups.set(app.compute_id, {
      compute_id: app.compute_id,
      display_name: app.display_name,
      icon_color: app.icon_color,
      icon_glyph: app.icon_glyph,
      icon_image_path: app.icon_image_path,
      listing_icon_url: app.listing_icon_url,
      instances: [app],
    });
  }

  const ordered = Array.from(groups.values());
  for (const group of ordered) {
    group.instances.sort(
      (left, right) =>
        updatedAtMs(right) - updatedAtMs(left) ||
        right.instance_id.localeCompare(left.instance_id),
    );
    // The group takes its identity from the instance it resumes, so a rename
    // shows up where a person expects it to. Every instance of one Compute
    // carries the same three values — personalization is keyed on the Compute
    // — so this is a choice of representative, not a tie-break.
    group.display_name = group.instances[0].display_name;
    group.icon_color = group.instances[0].icon_color;
    group.icon_glyph = group.instances[0].icon_glyph;
    group.icon_image_path = group.instances[0].icon_image_path;
    // The listing icon is the one value that can legitimately differ between
    // instances of one Compute: an App added from Discover carries it and an
    // App added by upload does not. Take the first one that has it rather than
    // the representative's, so one catalogue-added data set is enough to give
    // the whole tile the listing's icon instead of falling back to initials.
    group.listing_icon_url =
      group.instances.find((instance) => instance.listing_icon_url)
        ?.listing_icon_url ?? null;
  }
  ordered.sort(
    (left, right) =>
      updatedAtMs(right.instances[0]) - updatedAtMs(left.instances[0]) ||
      right.compute_id.localeCompare(left.compute_id),
  );
  return ordered;
}

/**
 * The other sets of data this App holds, excluding the one being looked at.
 *
 * Same ordering as the group, so "the next one down" means the same thing on
 * both screens.
 */
export function siblingsOf(
  apps: PersonalApp[],
  instance: PersonalApp,
): PersonalApp[] {
  const group = groupByApp(apps).find(
    (candidate) => candidate.compute_id === instance.compute_id,
  );
  if (!group) return [];
  return group.instances.filter(
    (candidate) => candidate.instance_id !== instance.instance_id,
  );
}
